import { Controller } from "@hotwired/stimulus";

// Opens the Help Scout Beacon panel on click. Falls back to the element's
// default href (e.g. help.faithstreet.com) if the beacon JS hasn't loaded.
//
// Turbo + Help Scout has a nasty interaction: if the beacon is open when
// the user navigates away, Turbo's snapshot captures the beacon iframes
// in their open state. On return, Turbo restores those iframes from cache
// but the beacon SDK has lost its handle on them — they're zombies. Once
// in this state, Beacon('close') and Beacon('open') are both no-ops, and
// the user is stuck with a half-open, non-interactive panel.
//
// Fix: on `turbo:before-cache`, fully destroy the beacon (which removes
// its iframes from the DOM), then re-init it whenever a controller
// instance connects so the SDK is freshly mounted on every page visit.
// We hook before-cache (not disconnect) because Turbo snapshots the DOM
// before controllers disconnect, so disconnect would be too late.
//
// Pass the beacon ID via a Stimulus value. Optionally pass a display
// style (e.g. "manual" to hide the floating launcher button):
//
//   <a href="https://help.faithstreet.com"
//      data-controller="helpscout"
//      data-helpscout-beacon-id-value="abc-123"
//      data-helpscout-display-style-value="manual"
//      data-action="click->helpscout#open">Send a message</a>
//
// The lifecycle (init + before-cache listener) is page-scoped, not
// element-scoped, even though Stimulus controllers are per-element. A
// page can have multiple triggers and the SDK can only be init'd once
// at a time — calling init/destroy redundantly across instances corrupts
// its internal state. We track active instances with a module-level
// counter so the lifecycle runs exactly once per page visit, regardless
// of how many trigger elements share the controller.

let activeInstances = 0;
let listenerAttached = false;
let beaconInitialized = false;

const destroyBeacon = () => {
  if (!window.Beacon) return;
  try {
    window.Beacon("destroy");
  } catch (e) {
    // Per the disconnect() comment, double-destroy can corrupt the SDK.
    // Surface it instead of letting a Turbo cache event throw uncaught.
    console.error(
      "[helpscout] Beacon destroy failed; SDK may be in bad state",
      e,
    );
  }
  beaconInitialized = false;
};

export default class extends Controller {
  static values = {
    beaconId: String,
    displayStyle: String,
  };

  connect() {
    if (!this.hasBeaconIdValue) return;
    activeInstances += 1;
    if (activeInstances === 1) {
      // First trigger on this page — init the beacon and arm the cleanup.
      this.initBeacon();
      document.addEventListener("turbo:before-cache", destroyBeacon);
      listenerAttached = true;
    }
  }

  disconnect() {
    if (!this.hasBeaconIdValue) return;
    activeInstances = Math.max(0, activeInstances - 1);
    if (activeInstances === 0 && listenerAttached) {
      // Last trigger is leaving — detach the cleanup listener so we don't
      // call Beacon('destroy') on pages that have no helpscout. Calling
      // destroy on an already-destroyed beacon corrupts the SDK and the
      // next init silently fails to mount.
      document.removeEventListener("turbo:before-cache", destroyBeacon);
      listenerAttached = false;
    }
  }

  initBeacon() {
    if (!window.Beacon) {
      // Beacon loader script hasn't run yet (async, ad blocker, CSP, CDN
      // outage). open() will retry on the first click.
      console.warn(
        "[helpscout] Beacon SDK not yet available at connect; will retry on click",
      );
      return;
    }
    if (this.hasDisplayStyleValue) {
      window.Beacon("config", { display: { style: this.displayStyleValue } });
    }
    window.Beacon("init", this.beaconIdValue);
    beaconInitialized = true;
  }

  open(event) {
    if (!window.Beacon) {
      // Beacon never loaded — fall back to the element's href.
      console.warn(
        "[helpscout] Beacon SDK unavailable; falling back to href",
        this.element.href,
      );
      return;
    }
    if (!beaconInitialized) {
      // Race: connect() ran before the Beacon loader finished. Init now.
      this.initBeacon();
    }
    event.preventDefault();
    window.Beacon("open");
  }
}
